import { Api } from '@monorepo/shared/apiClient';
import {
  OrganizationDocumentsCacheEvents,
  organizationDocumentsCacheRegister,
} from '@monorepo/shared/cacheRegisters/documents/organizationDocumentsCacheRegister';
import {
  SiteDocumentsCacheEvents,
  siteDocumentsCacheRegister,
} from '@monorepo/shared/cacheRegisters/documents/siteDocumentsCacheRegister';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { DocumentFileResponse } from 'mapistry-shared';
import { queryCache, QueryConfig, useQuery } from 'react-query';

type UseSiteFolderDocumentsListParams =
  Partial<Api.FetchProjectFolderDocumentsParams> & {
    config?: QueryConfig<DocumentFileResponse[], Api.ErrorResponse>;
  };

const KEY_START = 'site-folder-documents-list';

export const createKey = ({
  organizationId,
  projectId,
  folderId,
}: Api.FetchProjectFolderDocumentsParams) =>
  [KEY_START, organizationId, projectId, folderId] as const;

const fetcher: Api.DataHookQueryFn<
  typeof createKey,
  typeof Api.fetchProjectFolderDocuments
> = (_key, organizationId, projectId, folderId) =>
  Api.fetchProjectFolderDocuments({ organizationId, projectId, folderId });

export function useSiteFolderDocumentsList({
  organizationId,
  projectId,
  folderId,
  config: inputConfig,
}: UseSiteFolderDocumentsListParams) {
  const isEnabled = !!organizationId && !!projectId && !!folderId;
  const config = getQueryConfig(inputConfig, isEnabled);
  const key = isEnabled
    ? createKey({ organizationId, projectId, folderId })
    : undefined;
  const { data, ...queryInfo } = useQuery(key, fetcher, config);
  return { ...queryInfo, documents: data };
}

type InvalidateCacheParams = {
  organizationId: string;
  projectId?: string;
  folderId?: string;
};

export async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation([
      KEY_START,
      keyParams.organizationId,
      keyParams.projectId,
      keyParams.folderId,
    ]),
  );
}

siteDocumentsCacheRegister(
  [
    SiteDocumentsCacheEvents.CREATE,
    SiteDocumentsCacheEvents.UPDATE,
    SiteDocumentsCacheEvents.DELETE,
    SiteDocumentsCacheEvents.MOVE,
  ],
  {
    hookName: 'useSiteFolderDocumentsList',
    callback: (keyParams) => invalidateCache(keyParams),
  },
);

// Org-scoped folder file lists mirror Setup; refresh when org files change.
organizationDocumentsCacheRegister(
  [
    OrganizationDocumentsCacheEvents.CREATE,
    OrganizationDocumentsCacheEvents.UPDATE,
    OrganizationDocumentsCacheEvents.DELETE,
    OrganizationDocumentsCacheEvents.MOVE,
  ],
  {
    hookName: 'useSiteFolderDocumentsList',
    callback: (keyParams) => invalidateCache(keyParams),
  },
);
